import React, { useState, useEffect, useMemo } from 'react';
import { formatTime12h } from '../utils';

export default function StudentModal({ studentId, students, enrollments, batches, closeModal }) {
  const [apiData, setApiData] = useState({ payments: {}, attendance: {} });
  const [isLoading, setIsLoading] = useState(false);

  const student = students.find(s => (s.id || s._id) === studentId);
  const studentEnrollments = useMemo(() => {
    return enrollments.filter(e => e.studentId === studentId);
  }, [enrollments, studentId]);

  // Fetch Payment and Attendance Data from external APIs
  useEffect(() => {
    if (!studentId || studentEnrollments.length === 0) return;

    const fetchEnrollmentData = async () => {
      setIsLoading(true);
      try {
        const newPayments = {};
        const newAttendance = {};

        // Fetch data for all of the student's enrollments concurrently
        await Promise.all(studentEnrollments.map(async (enroll) => {
          const eId = enroll.id || enroll._id;
          // Fetch Payments with credentials
          const payRes = await fetch(`https://tsaapis.operate.live/api/payments?EnrolmentId=${eId}`, {
            credentials: 'include'
          });
          const payData = await payRes.json();
          newPayments[eId] = Array.isArray(payData)
            ? payData.filter(p => p.EnrolmentId === eId)
            : [];

          // Fetch Attendance with credentials
          const attRes = await fetch(`https://tsaapis.operate.live/api/attendance?EnrolmentId=${eId}`, {
            credentials: 'include'
          });
          const attData = await attRes.json();
          newAttendance[eId] = Array.isArray(attData)
            ? attData.filter(a => a.EnrolmentId === eId)
            : [];
        }));

        setApiData({ payments: newPayments, attendance: newAttendance });
      } catch (error) {
        console.error("Error fetching API data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEnrollmentData();
  }, [studentId, studentEnrollments]);

  if (!student) return null;

  const getCategory = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age >= 18 ? 'ADULT' : 'CHILD';
  };

  // --- PROGRESS BAR LOGIC ---
  const getProgressColor = (percent) => {
    if (percent >= 90) return '#ef4444'; // Red (Nearing empty)
    if (percent >= 75) return '#f59e0b'; // Amber/Orange
    return '#10b981'; // Green
  };


  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
        <button className="close-modal" onClick={closeModal}>&times;</button>

        {/* --- PROFILE HEADER WITH PICTURE --- */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '2rem', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '1.5rem' }}>
          <img
            src={student.profilePic || `https://ui-avatars.com/api/?name=${student.name.split(' ')[0]}+${student.name.split(' ')[1]||''}&background=3b82f6&color=fff&size=300`}
            alt={student.name}
            style={{
              width: '90px',
              height: '90px',
              borderRadius: '50%',
              border: '3px solid var(--accent-gold)',
              objectFit: 'cover',
              marginBottom: '10px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.3)'
            }}
          />
          <h2 style={{ fontSize: '1.6rem', margin: '0 0 5px 0', borderBottom: 'none', padding: 0 }}>{student.name}</h2>
          <span style={{ color: 'var(--accent-gold)', fontWeight: 'bold', fontSize: '1.1rem' }}>ID: {student.regId}</span>
        </div>

        {/* --- PROFILE DETAILS GRID --- */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '10px' }}>
            <span style={{ color: 'var(--text-gray)', fontSize: '0.85rem' }}>Type</span>
            <span className="badge" style={{ alignSelf: 'flex-start', marginTop: '5px' }}>{getCategory(student.dob)}</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '10px' }}>
            <span style={{ color: 'var(--text-gray)', fontSize: '0.85rem' }}>Date of Birth</span>
            <span style={{ color: 'white', marginTop: '5px' }}>{student.dob || 'N/A'}</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '10px' }}>
            <span style={{ color: 'var(--text-gray)', fontSize: '0.85rem' }}>Mobile</span>
            <span style={{ color: 'white', marginTop: '5px' }}>{student.mobile}</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '10px' }}>
            <span style={{ color: 'var(--text-gray)', fontSize: '0.85rem' }}>WhatsApp No.</span>
            <span style={{ color: 'white', marginTop: '5px' }}>{student.whatsapp || 'N/A'}</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '10px' }}>
            <span style={{ color: 'var(--text-gray)', fontSize: '0.85rem' }}>Emergency No.</span>
            <span style={{ color: '#ef4444', fontWeight: 'bold', marginTop: '5px' }}>{student.emergencyContact || 'N/A'}</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '10px' }}>
            <span style={{ color: 'var(--text-gray)', fontSize: '0.85rem' }}>Email</span>
            <span style={{ color: 'white', marginTop: '5px' }}>{student.email}</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 2' }}>
            <span style={{ color: 'var(--text-gray)', fontSize: '0.85rem' }}>Address</span>
            <span style={{ color: 'white', lineHeight: '1.4', marginTop: '5px' }}>{student.address || 'N/A'}</span>
          </div>
        </div>

        {/* --- ENROLLMENT HISTORY (BATCHES, PAYMENT, ATTENDANCE) --- */}
        <h3 style={{ fontSize: '1.3rem', color: 'white', borderBottom: '1px solid var(--accent-gold)', paddingBottom: '10px', marginBottom: '1rem' }}>
          Enrollment History {isLoading && <span style={{ fontSize: '0.9rem', color: 'var(--text-gray)' }}>(Loading data...)</span>}
        </h3>

        {studentEnrollments.length === 0 ? (
          <p style={{ color: 'var(--text-gray)' }}>No active enrollments found for this student.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            {studentEnrollments.map((enroll) => {
              const eId = enroll.id || enroll._id;
              const batch = batches.find(b => (b.id || b._id) === enroll.batchId);
              if (!batch) return null;

              const enrollmentPayments = apiData.payments[eId] || [];
              const enrollmentAttendance = apiData.attendance[eId] || [];

              // --- HOURS USED PROGRESS LOGIC ---
              const usedHrs = Number(enroll.usedHours || enroll.UsedHours) || 0;
              const balHrs = Number(enroll.balanceHours || enroll.BalanceHours) || 0;
              const totalHrs = usedHrs + balHrs;
              const percentUsed = totalHrs > 0 ? Math.min(100, Math.round((usedHrs / totalHrs) * 100)) : 0;

              const dynamicUsedHours = usedHrs.toFixed(1);
              const balanceHoursFormatted = balHrs.toFixed(1);

              return (
                <div key={eId} style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '1.5rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>

                  {/* Batch Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', flexWrap: 'wrap', gap: '15px' }}>
                    <div>
                      <h4 style={{ color: 'var(--accent-gold)', fontSize: '1.2rem', margin: '0 0 5px 0' }}>{batch.Title || batch.title}</h4>
                      <span className="badge" style={{ fontSize: '0.75rem' }}>{batch.Type || batch.type}</span>
                    </div>
                    
                    <div style={{ textAlign: 'right', minWidth: '160px' }}>
                      <div style={{ fontSize: '0.9rem', color: 'var(--text-gray)' }}>{enroll.startDay || enroll.EnrolledDate?.split('T')[0]} to {enroll.endDay || enroll.ExpiryDate?.split('T')[0]}</div>
                      
                      {/* NEW: HOURS USED PROGRESS BAR */}
                      <div style={{ marginTop: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', fontSize: '0.75rem', marginBottom: '5px', fontWeight: 'bold' }}>
                          <span style={{ color: '#ef4444' }}>{dynamicUsedHours} Used</span>
                          <span style={{ color: '#10b981' }}>{balanceHoursFormatted} Bal</span>
                        </div>
                        <div style={{ width: '100%', height: '6px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '3px', overflow: 'hidden' }}>
                          <div style={{
                            height: '100%',
                            width: `${percentUsed}%`,
                            backgroundColor: getProgressColor(percentUsed),
                            transition: 'width 0.5s ease-out, background-color 0.5s ease'
                          }}></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payment Info Table */}
                  <h5 style={{ color: 'white', marginTop: '1.5rem', marginBottom: '0.5rem', fontSize: '1rem' }}>
                    <i className="fas fa-wallet" style={{ color: 'var(--accent-blue)', marginRight: '5px' }}></i> Payment Info
                  </h5>
                  <div className="table-responsive" style={{ marginBottom: '1.5rem' }}>
                    <table style={{ background: 'transparent', width: '100%' }}>
                      <thead>
                        <tr>
                          <th style={{ background: 'rgba(255,255,255,0.05)', padding: '8px', textAlign: 'left' }}>Date Paid</th>
                          <th style={{ background: 'rgba(255,255,255,0.05)', padding: '8px', textAlign: 'left' }}>Mode</th>
                          <th style={{ background: 'rgba(255,255,255,0.05)', padding: '8px', textAlign: 'left' }}>Ref</th>
                          <th style={{ background: 'rgba(255,255,255,0.05)', padding: '8px', textAlign: 'left' }}>Amount</th>
                          <th style={{ background: 'rgba(255,255,255,0.05)', padding: '8px', textAlign: 'left' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading ? (
                          <tr><td colSpan="5" style={{ padding: '8px', textAlign: 'center', color: 'var(--text-gray)' }}>Fetching payments...</td></tr>
                        ) : enrollmentPayments.length === 0 ? (
                          <tr><td colSpan="5" style={{ padding: '8px', textAlign: 'center', color: 'var(--text-gray)' }}>No payment records found.</td></tr>
                        ) : (
                          enrollmentPayments.map((pay, i) => (
                            <tr key={pay._id || i}>
                              <td style={{ padding: '8px' }}>{new Date(pay.PaidDT).toLocaleDateString()}</td>
                              <td style={{ padding: '8px' }}><span className="badge">{pay.Mode}</span></td>
                              <td style={{ padding: '8px', fontFamily: 'monospace' }}>{pay.TransactionRef || 'N/A'}</td>
                              <td style={{ padding: '8px' }}>
                                <strong style={{ color: 'var(--accent-gold)' }}>₹{pay.PaidAmount}</strong>
                                <br /><small style={{ color: 'var(--text-gray)' }}>{pay.Remarks}</small>
                              </td>
                              <td style={{ padding: '8px' }}><span className="badge">{pay.Status}</span></td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Attendance Info Table */}
                  <h5 style={{ color: 'white', marginBottom: '0.5rem', fontSize: '1rem' }}>
                    <i className="fas fa-calendar-check" style={{ color: 'var(--accent-blue)', marginRight: '5px' }}></i> Attendance Grid
                  </h5>
                  <div className="table-responsive">
                    <table style={{ background: 'transparent', width: '100%' }}>
                      <thead>
                        <tr>
                          <th style={{ background: 'rgba(255,255,255,0.05)', padding: '8px', textAlign: 'left' }}>Date</th>
                          <th style={{ background: 'rgba(255,255,255,0.05)', padding: '8px', textAlign: 'left' }}>Start</th>
                          <th style={{ background: 'rgba(255,255,255,0.05)', padding: '8px', textAlign: 'left' }}>End</th>
                          <th style={{ background: 'rgba(255,255,255,0.05)', padding: '8px', textAlign: 'left' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading ? (
                          <tr><td colSpan="4" style={{ padding: '8px', textAlign: 'center', color: 'var(--text-gray)' }}>Fetching attendance...</td></tr>
                        ) : enrollmentAttendance.length === 0 ? (
                          <tr><td colSpan="4" style={{ padding: '8px', textAlign: 'center', color: 'var(--text-gray)' }}>No attendance records.</td></tr>
                        ) : (
                          enrollmentAttendance.map((att, i) => {
                            const loginDate = new Date(att.LoginDT);
                            const logoutDate = att.LogoutDT ? new Date(att.LogoutDT) : null;
                            let sessionTotal = 0;

                            if (logoutDate) {
                              sessionTotal = ((logoutDate - loginDate) / (1000 * 60 * 60)).toFixed(1);
                            }

                            return (
                              <tr key={att._id || i}>
                                <td style={{ padding: '8px' }}>{loginDate.toLocaleDateString()}</td>
                                <td style={{ padding: '8px' }}>
                                  {loginDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                </td>
                                <td style={{ padding: '8px' }}>
                                  {logoutDate ? logoutDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : 'Active'}
                                </td>
                                <td style={{ padding: '8px', color: 'var(--accent-gold)', fontWeight: 'bold' }}>{sessionTotal} Hrs</td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}